import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RunnerJobCardComponent } from '../../components/runner-job-card/runner-job-card.component';
import { KonfigPillComponent } from '../../components/konfig-pill/konfig-pill.component';
import {
  FilterDropdownComponent,
  FilterDropdownOption,
} from '../../components/filter-dropdown/filter-dropdown.component';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import {
  ResultStatus,
  RunnerCase,
  RunnerState,
  RunnerStep,
  TEST_RUN_CONFIGURATIONS,
} from '../../models/test-run.model';
import { TestRunService } from '../../services/test-run.service';
import { TestlaufRunnerService } from '../../services/testlauf-runner.service';
import { formatDurationMs } from '../../shared/duration.util';
import { TEST_RUN_STATUS_ICON } from '../../shared/test-run-outcome';
import { findRouteParam } from '../../shared/route-params';
import { statusKey } from '../test-results/test-results.types';
import { resolveIterationValues } from '../testlauf-runner/iteration-values';
import { decidedSteps, leafSteps } from '../testlauf-runner/runner-steps';
import { NumberedStep, numberSteps } from '../testlauf-runner/step-numbering';
import { callLine, callReason } from '../testlauf-runner/call-reason';
import {
  HeaderPath,
  ResultNode,
  ResultRow,
  headerPath,
  isHidden,
  isStructured,
  passLabel,
  resultRows,
} from '../testlauf-runner/result-rows';

interface CaseSection {
  runnerCase: RunnerCase;
  entries: NumberedStep[];
  /** Nur fuer Faelle mit Aufruf-Zustand (NEX-2282); Bestandslaeufe zeigen `entries` wie bisher. */
  rows: ResultRow<NumberedStep>[] | null;
}

function nodeOf(entry: NumberedStep): ResultNode {
  const { step } = entry;
  return {
    id: step.id,
    parentId: entry.parentId,
    kind: step.kind,
    callState: step.callState,
    passIndex: step.passIndex,
    loopPhase: step.loopPhase,
    iterationIndex: step.iterationIndex,
  };
}

/**
 * Die Ergebnisse eines abgeschlossenen Durchlaufs, nur zum Lesen. Aufbau und Klassen
 * folgen der Testergebnis-Detailseite; die Zahlen sind dieselben wie auf der
 * Abschluss-Karte des Runners, damit beide Ansichten nie Verschiedenes melden.
 */
@Component({
  selector: 'app-testlauf-ergebnisse',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    NgTemplateOutlet,
    MatIcon,
    MatProgressSpinner,
    TranslatePipe,
    ProjectNavComponent,
    RunnerJobCardComponent,
    KonfigPillComponent,
    FilterDropdownComponent,
  ],
  templateUrl: './testlauf-ergebnisse.component.html',
  styleUrls: ['../test-result-detail/test-result-detail.component.scss', './testlauf-ergebnisse.component.scss'],
})
export class TestlaufErgebnisseComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly runnerService = inject(TestlaufRunnerService);
  private readonly testRunService = inject(TestRunService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  protected readonly statusKey = statusKey;

  protected readonly projectId = this.findParam('projectId');
  private readonly runId = this.findParam('runId');
  private readonly executionId = this.findParam('executionId');

  protected readonly executionsLink = ['/projects', this.projectId, 'test-runs', this.runId, 'executions'];
  protected readonly testResultLink = ['/projects', this.projectId, 'test-results', this.runId];

  protected readonly loading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly state = signal<RunnerState | null>(null);
  /** Das Testergebnis-Detail zeigt nur den neuesten Durchlauf; nur dort fuehrt der Link zu den eigenen Screenshots. */
  protected readonly testResultLinkShown = signal(false);

  protected readonly automated = computed(() => this.state()?.runKind === 'AUTOMATED');
  protected readonly environment = computed(() => this.state()?.environment?.trim() || '—');
  protected readonly outcome = computed<ResultStatus>(() => this.state()?.status ?? 'NOT_RUN');
  protected readonly outcomeIcon = computed(() => TEST_RUN_STATUS_ICON[this.outcome()]);
  /** Ein abgebrochener Durchlauf bleibt SKIPPED, heisst aber wie in der Testlauf-Uebersicht „Abgebrochen" (NEX-1998). */
  protected readonly outcomeLabel = computed(() =>
    this.state()?.abortedAt ? 'testRuns.status.aborted' : 'testResults.result.' + statusKey(this.outcome()),
  );
  protected readonly duration = computed(() => formatDurationMs(this.state()?.durationMs));

  private readonly leaves = computed(() => leafSteps(this.state()?.cases ?? []));
  protected readonly failed = computed(() => this.countLeaves('FAILED'));
  protected readonly blocked = computed(() => this.countLeaves('BLOCKED'));
  protected readonly skipped = computed(() => this.countLeaves('SKIPPED'));
  protected readonly hasStepResults = computed(() => decidedSteps(this.state()?.cases ?? []).length > 0);

  protected readonly sections = computed<CaseSection[]>(() =>
    [...(this.state()?.cases ?? [])]
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((runnerCase) => {
        const entries = numberSteps(resolveIterationValues(runnerCase.steps));
        return { runnerCase, entries, rows: isStructured(entries, nodeOf) ? resultRows(entries, nodeOf) : null };
      }),
  );

  private readonly runConfigurations = signal<string[]>([]);

  /** Konfigurationen des Laufs und, aus der Zeit vor der Sperre, der Ergebnisse; feste Reihenfolge, Unbekanntes hinten. */
  protected readonly configurationOptions = computed<FilterDropdownOption[]>(() => {
    const known: readonly string[] = TEST_RUN_CONFIGURATIONS;
    const rank = (value: string) => (known.includes(value) ? known.indexOf(value) : known.length);
    return [...new Set([...this.runConfigurations(), ...this.resultConfigurations()])]
      .sort((a, b) => rank(a) - rank(b))
      .map((value) => ({ value, label: value }));
  });
  protected readonly configurationFilterShown = computed(() => this.configurationOptions().length > 1);
  protected readonly configurationFilter = signal('');
  protected readonly visibleSections = computed(() => {
    const filter = this.configurationFilter();
    return filter ? this.sections().filter((section) => section.runnerCase.configuration === filter) : this.sections();
  });

  /** Zugeklappte Runden- und Rückweg-Koepfe; nur in der Ansicht, Standard offen. */
  protected readonly collapsed = signal<ReadonlySet<string>>(new Set());

  constructor() {
    this.runnerService
      .getState(this.projectId, this.runId, this.executionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (runnerState) => {
          if (runnerState.live) {
            this.openRunner();
          } else if (runnerState.runKind === 'AUTOMATED') {
            this.showRunnerRunOrOpenRunner(runnerState);
          } else {
            this.show(runnerState);
          }
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  /**
   * Automatisierte Durchlaeufe gehoeren in den Runner, ausser sie stammen von einem
   * verbundenen Rechner (TCDL): deren Schritte gibt es nur hier. Pipeline-Laeufe bleiben
   * in der Live-Ansicht, dort haengt die CI-Zuordnung. Ohne Quelle gilt das bisherige Ziel.
   */
  private showRunnerRunOrOpenRunner(runnerState: RunnerState): void {
    this.testRunService
      .get(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => {
          if (run.source !== 'TCDL') {
            this.openRunner();
            return;
          }
          this.show(runnerState);
          this.showTestResultLinkIfNewest();
        },
        error: () => this.openRunner(),
      });
  }

  /** Scheitert das Laden, fehlt nur der Link. */
  private showTestResultLinkIfNewest(): void {
    this.testRunService
      .listExecutions(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (executions) => {
          const newestNr = Math.max(...executions.map((execution) => execution.nr));
          this.testResultLinkShown.set(executions.some((e) => e.id === this.executionId && e.nr === newestNr));
        },
        error: () => {
          // Bewusst still: die Ergebnisse bleiben stehen.
        },
      });
  }

  private show(runnerState: RunnerState): void {
    this.state.set(runnerState);
    this.loading.set(false);
    if (this.resultConfigurations().length > 0) {
      this.loadRunConfigurations();
    }
  }

  /** Scheitert das Laden, bleiben die Konfigurationen der Ergebnisse als Auswahl. */
  private loadRunConfigurations(): void {
    this.testRunService
      .getSettings(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (settings) => this.runConfigurations.set(settings.configurations),
        error: () => {
          // Bewusst still: die Ergebnisse bleiben stehen.
        },
      });
  }

  private resultConfigurations(): string[] {
    return (this.state()?.cases ?? []).flatMap((runnerCase) =>
      runnerCase.configuration ? [runnerCase.configuration] : [],
    );
  }

  /** Ersetzt die Ergebnis-URL, damit "Zurueck" im Browser nicht hier landet und erneut umleitet. */
  private openRunner(): void {
    void this.router.navigate(['/projects', this.projectId, 'test-runs', this.runId, 'executions', this.executionId], {
      replaceUrl: true,
    });
  }

  protected toggle(id: string): void {
    this.collapsed.update((ids) => {
      const next = new Set(ids);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  protected isShown(row: ResultRow<NumberedStep>): boolean {
    return !isHidden(row, this.collapsed());
  }

  protected headerOf(row: ResultRow<NumberedStep>): HeaderPath {
    return headerPath(row.item.step.path, row.item.step.description, this.translate);
  }

  protected passLabelOf(row: ResultRow<NumberedStep>): string {
    return passLabel(row, this.translate);
  }

  protected reasonOf(step: RunnerStep): string {
    return callReason(step.callState, step.note, step.callConditions, this.translate);
  }

  protected lineOf(step: RunnerStep): string {
    return callLine(step.description, step.callState, step.note, step.callConditions, this.translate);
  }

  protected stepDuration(step: RunnerStep): string {
    return formatDurationMs(step.durationMs);
  }

  private countLeaves(status: ResultStatus): number {
    return this.leaves().filter((step) => step.actualStatus === status).length;
  }

  private findParam(name: string): string {
    return findRouteParam(this.route, name) ?? '';
  }
}
